/**
 * Alert_Rule editing (route `/alert-rules/:id/edit`; Feature: granular-crud-api, tasks 14.1, 14.2;
 * Requirements 11.4, 11.6, 11.7, 11.8; Design: §11).
 *
 * The rule is loaded first — `GET /v1/alert-rules/{id}` — for its current values and its `origin`, so a
 * manifest-managed rule carries the Adoption warning above the form: writing it through the API removes
 * it from the deployment manifest's reconciliation for good (Req 11.8). A Terraform-managed rule carries
 * the drift warning and an acknowledgement the submit is gated on (terraform-provider Req 13.1, 13.3),
 * and a write that hands management over is confirmed in place (Req 13.5). `PATCH /v1/alert-rules/{id}`
 * runs through {@link useWrite}, so a failure keeps the draft and shows the error (Req 11.6).
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { GetAlertRuleResponse } from '../api/types';
import { useApiClient } from '../api/use-api-client';
import { useCanWrite } from '../auth/use-can-write';
import {
  ProvenanceNotice,
  ProvenanceTransferNotice,
  requiresAcknowledgement,
  transferredFromTerraform,
} from '../components/ProvenanceNotice';
import { AlertRuleForm } from '../components/AlertRuleForm';
import { ErrorState, LoadingState } from '../components/LoadStates';
import { TerraformSection } from '../components/TerraformSection';
import { useResource } from '../lib/use-resource';
import { useWrite } from '../lib/use-write';

export function AlertRuleEditPage(): JSX.Element {
  const client = useApiClient();
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const { id } = useParams();
  const ruleId = id ?? '';
  const write = useWrite();
  /** The Terraform acknowledgement (Req 13.3). Ignored for every other origin. */
  const [acknowledged, setAcknowledged] = useState(false);
  /** Set when the write reported an Ownership_Transfer away from Terraform (Req 13.5). */
  const [transferred, setTransferred] = useState(false);

  const loaded = useResource<GetAlertRuleResponse>(
    () => client.getAlertRule(ruleId),
    [client, ruleId],
  );

  if (loaded.status === 'loading') {
    return (
      <section>
        <h1>Edit alert rule</h1>
        <LoadingState label="Loading alert rule…" />
      </section>
    );
  }

  if (loaded.status === 'error') {
    return (
      <section>
        <h1>Edit alert rule</h1>
        <ErrorState
          summary="Could not load this alert rule."
          message={loaded.message}
          isAuthError={loaded.isAuthError}
        />
      </section>
    );
  }

  const rule = loaded.data.alertRule;

  return (
    <section>
      <h1>Edit alert rule</h1>
      {!canWrite ? (
        <p role="note" className="muted" data-testid="read-only-notice">
          Your role has read-only access, so this alert rule cannot be changed from the dashboard.
        </p>
      ) : null}
      <AlertRuleForm
        mode="edit"
        initial={rule}
        readOnly={!canWrite}
        submitting={write.state.submitting}
        fieldErrors={write.state.fieldErrors}
        errorMessage={write.state.errorMessage}
        notice={
          canWrite ? (
            transferred ? (
              // The loaded rule still reads `terraform`; the transfer already happened, so the
              // confirmation replaces the warning rather than sitting beside it.
              <ProvenanceTransferNotice />
            ) : (
              <ProvenanceNotice
                origin={rule.origin}
                entity="alert rule"
                acknowledged={acknowledged}
                onAcknowledge={setAcknowledged}
              />
            )
          ) : undefined
        }
        submitDisabled={requiresAcknowledgement(rule.origin) && !acknowledged}
        onSubmit={(patch) =>
          void write.run(
            () => client.updateAlertRule(ruleId, patch),
            (response) => {
              // A Terraform hand-over is confirmed in place (Req 13.5); anything else returns to the
              // list exactly as before.
              if (transferredFromTerraform(response)) setTransferred(true);
              else navigate('/alert-rules');
            },
          )
        }
        onCancel={() => navigate('/alert-rules')}
      />

      {/*
       * The Terraform adoption surface (terraform-provider Req 14.2), below the form. This is where the
       * Alert_Rules list's per-row "Terraform" link lands, via `#terraform` — a rule has no detail page,
       * so the edit page is the only surface that holds its full configuration. Rendered from the already
       * loaded `rule`, so it adds no request (Req 14.7), and shown regardless of `canWrite`: reading how
       * to adopt a rule into Terraform is not a write, and a viewer holding an editor API key is exactly
       * the reader Req 14.6's second half exists for.
       */}
      <TerraformSection subject={{ kind: 'alert-rule', rule }} />
    </section>
  );
}
